<script setup lang="ts">
import { ptBR } from 'date-fns/locale'
import DatePicker from 'primevue/datepicker'
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { isMobileWindow } from '@/utils/screen/window'
import { useUserStore } from '@/store/user'
import DialogDatepicker from './DialogDatepicker.vue'
import { useField } from 'vee-validate'

const props = defineProps<{
  modelValue?: Date[] | null
  alreadySelectedPeriod?: Date | string[] | {}
  placeholder: string
  name: string
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', period: Date[]): void
  (e: 'change-date', period: Date[]): void
}>()

const { getHasNewDatepicker } = storeToRefs(useUserStore())
const dates = ref(<Date[] | null>(props.modelValue ?? null))
const today = new Date()
const datePickerRef = ref()

const { value: inputValue, errorMessage } = useField<Date[]>(props.name)

function handleClear(event?: Event) {
  if (event) event.stopPropagation()
  dates.value = null
  emits('update:modelValue', [])
  setTimeout(() => {
    datePickerRef.value?.show?.()
  }, 0)
}

function handleDialogDateChange(period: Date[]) {
  dates.value = period
  emits('update:modelValue', period)
  emits('change-date', period)
}

onMounted(() => {
  if (props.alreadySelectedPeriod && Array.isArray(props.alreadySelectedPeriod) && props.alreadySelectedPeriod.length === 2) {
    dates.value = (props.alreadySelectedPeriod as (Date | string)[]).map((date) => (typeof date === 'string' ? new Date(date) : date)) as Date[]
  }
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (Array.isArray(newValue) && newValue.length === 2) {
      dates.value = newValue
    }
  },
)

watch(dates, (newValue) => {
  if (Array.isArray(newValue) && newValue.length === 2 && newValue[0] && newValue[1]) {
    emits('update:modelValue', newValue)
    emits('change-date', newValue)
  }
})

const numberOfMonths = computed(() => {
  return isMobileWindow() ? 1 : 2
})
</script>

<template>
  <div v-if="getHasNewDatepicker" class="height-50 relative w-full">
    <DatePicker
      ref="datePickerRef"
      :locale="ptBR"
      :placeholder="props.placeholder"
      id="datepicker-12h"
      v-model="dates"
      dateFormat="dd/mm/yy"
      selection-mode="range"
      :manual-input="false"
      showTime
      hourFormat="24"
      fluid
      :numberOfMonths="numberOfMonths"
      showIcon
      showButtonBar
      iconDisplay="input"
      @clear-click="handleClear"
      :max-date="today">
      <template #clearbutton="{ keydownCallback }">
        <button
          type="button"
          class="p-button p-component p-button-secondary p-button-label p-button-text p-button-sm p-datepicker-clear-button"
          @click="handleClear"
          @keydown="keydownCallback"
          tabindex="0">
          Limpar
        </button>
      </template>
    </DatePicker>
    <p v-if="errorMessage" class="absolute text-title-xsm4 font-medium text-red">
      {{ errorMessage }}
    </p>
  </div>
  <div v-else>
    <DialogDatepicker
      :already-selected-period="alreadySelectedPeriod"
      :placeholder="props.placeholder"
      :name="props.name"
      @change-date="handleDialogDateChange" />
  </div>
</template>

<style>
#datepicker-12h {
  width: 100%;
  max-width: 100%;
  height: 50px;
  --p-datepicker-date-selected-background: #123add;
  --p-datepicker-date-range-selected-background: #ecfbfd;
  --p-datepicker-date-range-selected-color: #123add;
  --p-inputtext-focus-border-color: #123add;
}
#datepicker-12h_panel {
  --p-datepicker-date-selected-background: #123add;
  --p-datepicker-date-range-selected-background: #ecfbfd;
  --p-datepicker-date-range-selected-color: #123add;
  --p-inputtext-focus-border-color: #123add;
}

#datepicker-12h input {
  border-radius: var(--p-border-radius-none);
}
</style>
